<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="headline">
            <v-icon left>{{ icons.mdiFolderOpen }}</v-icon>
            Data Browser
          </v-card-title>

          <v-card-text>
            <!-- Breadcrumb nav -->
            <v-breadcrumbs :items="breadcrumbs" divider="/">
              <template v-slot:[`item`]="{ item }">
                <v-breadcrumbs-item
                  :disabled="item.disabled"
                  class="breadcrumb-link"
                  @click="!item.disabled && navigateTo(item.path)"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <!-- Search -->
            <v-text-field
              v-model="search"
              :prepend-inner-icon="icons.mdiMagnify"
              label="Filter entries"
              clearable
              dense
              outlined
              class="mb-2"
              style="max-width: 400px;"
            />

            <!-- Loading -->
            <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

            <!-- Empty state -->
            <v-alert v-if="!loading && filteredEntries.length === 0" type="info" outlined>
              {{ search ? 'No entries match your filter.' : 'This directory is empty.' }}
            </v-alert>

            <!-- File table -->
            <v-data-table
              v-if="filteredEntries.length > 0"
              :headers="headers"
              :items="filteredEntries"
              :items-per-page="50"
              :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
              class="elevation-1"
              @click:row="onRowClick"
            >
              <template v-slot:[`item.name`]="{ item }">
                <span class="entry-name">
                  <v-icon small :color="item.type === 'dir' ? 'amber darken-2' : fileIconColor(item.ext)">
                    {{ item.type === 'dir' ? icons.mdiFolder : fileIcon(item.ext) }}
                  </v-icon>
                  {{ item.name }}
                </span>
              </template>

              <template v-slot:[`item.sizeFormatted`]="{ item }">
                {{ item.type === 'dir' ? '—' : item.sizeFormatted }}
              </template>

              <template v-slot:[`item.mtime`]="{ item }">
                {{ formatDate(item.mtime) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="item.type === 'file'"
                  color="primary"
                  small
                  :loading="downloading === item.path"
                  @click.stop="download(item)"
                >
                  <v-icon left small>{{ icons.mdiDownload }}</v-icon>
                  Download
                </v-btn>
                <v-btn
                  v-if="item.type === 'dir'"
                  color="secondary"
                  small
                  @click.stop="navigateTo(item.path)"
                >
                  <v-icon left small>{{ icons.mdiFolderOpenOutline }}</v-icon>
                  Open
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  mdiFolderOpen,
  mdiFolder,
  mdiFolderOpenOutline,
  mdiDownload,
  mdiMagnify,
  mdiFilePdfBox,
  mdiFileDelimited,
  mdiCodeJson,
  mdiFileDocumentOutline,
  mdiFileImage,
  mdiFileOutline,
  mdiZipBox,
  mdiXml,
} from '@mdi/js';
import { mapState, mapActions } from 'vuex';
import { toLocalDateTime } from '../../util/shared/vue-global';

const FILE_ICONS = {
  pdf: { icon: mdiFilePdfBox, color: 'red darken-1' },
  csv: { icon: mdiFileDelimited, color: 'green darken-1' },
  json: { icon: mdiCodeJson, color: 'orange darken-1' },
  txt: { icon: mdiFileDocumentOutline, color: 'grey' },
  png: { icon: mdiFileImage, color: 'blue darken-1' },
  jpg: { icon: mdiFileImage, color: 'blue darken-1' },
  jpeg: { icon: mdiFileImage, color: 'blue darken-1' },
  tiff: { icon: mdiFileImage, color: 'blue darken-1' },
  tif: { icon: mdiFileImage, color: 'blue darken-1' },
  zip: { icon: mdiZipBox, color: 'purple darken-1' },
  xml: { icon: mdiXml, color: 'teal darken-1' },
};

export default {
  name: 'DataBrowser',
  data: () => ({
    icons: {
      mdiFolderOpen,
      mdiFolder,
      mdiFolderOpenOutline,
      mdiDownload,
      mdiMagnify,
      mdiFileOutline,
    },
    search: '',
    downloading: null,
    headers: [
      {
        text: 'Name', value: 'name', sortable: true,
      },
      {
        text: 'Type', value: 'ext', sortable: true, width: '80px',
      },
      {
        text: 'Size', value: 'sizeFormatted', sortable: false, width: '110px',
      },
      {
        text: 'Modified', value: 'mtime', sortable: true, width: '180px',
      },
      {
        text: 'Actions', value: 'actions', sortable: false, width: '130px',
      },
    ],
  }),
  computed: {
    ...mapState('dataBrowser', ['entries', 'currentPath', 'loading']),
    breadcrumbs() {
      const crumbs = [{ text: 'data', path: '', disabled: this.currentPath === '' }];
      if (this.currentPath) {
        const parts = this.currentPath.split('/');
        parts.forEach((part, i) => {
          const p = parts.slice(0, i + 1).join('/');
          crumbs.push({ text: part, path: p, disabled: i === parts.length - 1 });
        });
      }
      return crumbs;
    },
    filteredEntries() {
      if (!this.search) return this.entries;
      const q = this.search.toLowerCase();
      return this.entries.filter((e) => e.name.toLowerCase().includes(q));
    },
  },
  created() {
    this.fetchEntries('');
  },
  methods: {
    ...mapActions('dataBrowser', ['fetchEntries', 'downloadFile']),
    navigateTo(p) {
      this.search = '';
      this.fetchEntries(p);
    },
    onRowClick(item) {
      if (item.type === 'dir') {
        this.navigateTo(item.path);
      }
    },
    async download(item) {
      this.downloading = item.path;
      await this.downloadFile(item.path);
      this.downloading = null;
    },
    fileIcon(ext) {
      return (FILE_ICONS[ext] || {}).icon || mdiFileOutline;
    },
    fileIconColor(ext) {
      return (FILE_ICONS[ext] || {}).color || 'grey darken-1';
    },
    formatDate(iso) {
      if (!iso) return '';
      return toLocalDateTime(iso);
    },
  },
};
</script>

<style scoped>
.entry-name {
  cursor: pointer;
}
.breadcrumb-link {
  cursor: pointer;
}
</style>
